import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, SlidersHorizontal } from "lucide-react";
import { ACCENTS, categoryBySlug, subName, CATEGORY_IMAGES, PRODUCT_IMAGES } from "@/data/catalog";
import { BUILDER_CONFIGS } from "@/data/builderConfigs";
import { useCart } from "@/context/CartContext";
import { useProductImages, productImageUrl } from "@/context/ProductImagesContext";
import { money, DISCOUNT_RULES } from "@/lib/api";

const ProductCard = ({ product, index = 0 }) => {
  const cat = categoryBySlug(product.category_slug);
  const accent = ACCENTS[cat?.accent || "cyan"];
  const Icon = cat?.icon;
  const { addItem } = useCart();
  const { images: customImages } = useProductImages();
  const navigate = useNavigate();
  const hasBuilder = Boolean(BUILDER_CONFIGS[product.slug]) || Boolean(product.configurable);
  const tiers = DISCOUNT_RULES[product.category_slug];
  const img = productImageUrl(
    customImages,
    product.slug,
    PRODUCT_IMAGES[product.slug] || CATEGORY_IMAGES[product.category_slug]
  );

  const handleAdd = () => {
    addItem({
      key: product.slug,
      slug: product.slug,
      name: product.name,
      unitPrice: product.price,
      qty: 1,
      categorySlug: product.category_slug,
      configLabel: subName(product.category_slug, product.sub_slug) || cat?.name,
    });
  };

  return (
    <motion.article
      data-testid={`product-card-${product.slug}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.45, delay: (index % 4) * 0.06 }}
      className={`group relative flex flex-col border border-white/10 bg-ink-2 transition-colors duration-300 hover:${accent.border}`}
    >
      <div className="relative flex aspect-[4/3] items-center justify-center overflow-hidden bg-ink-3">
        {img && (
          <img
            src={img}
            alt={product.name}
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover opacity-90 transition-transform duration-700 group-hover:scale-105"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/20 to-transparent" />
        <span className="absolute left-3 top-3 font-mono text-[10px] tracking-widest text-zinc-300">
          {String(index + 1).padStart(2, "0")}
        </span>
        <span className="absolute right-3 top-3 font-mono text-[10px] tracking-widest text-zinc-300">
          300 DPI
        </span>
        <span className={`absolute left-3 top-8 h-3 w-3 border-l-2 border-t-2 ${accent.border}`} />
        <span className={`absolute bottom-3 right-3 h-3 w-3 border-b-2 border-r-2 ${accent.border}`} />
        {!img && Icon && (
          <Icon
            className={`h-16 w-16 ${accent.text} opacity-80 transition-transform duration-500 group-hover:scale-110 group-hover:-rotate-6`}
            strokeWidth={1.25}
          />
        )}
        {product.badge && (
          <span className={`absolute bottom-3 left-3 ${accent.bg} px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest text-ink`}>
            {product.badge}
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col p-4">
        <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
          {subName(product.category_slug, product.sub_slug) || cat?.name}
        </p>
        <h3 className="mt-1 font-display text-lg font-bold uppercase leading-tight text-white">
          {product.name}
        </h3>
        <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-zinc-400">{product.blurb}</p>
        {tiers && (
          <p
            data-testid={`product-bulk-tiers-${product.slug}`}
            className="mt-2 font-mono text-[9px] uppercase tracking-wider text-brand-yellow"
          >
            Bulk pricing available
          </p>
        )}
        <div className="mt-auto flex items-center justify-between pt-4">
          <span className="font-mono text-sm font-bold text-brand-yellow">
            {hasBuilder ? `from ${money(product.price)}` : money(product.price)}
          </span>
          {hasBuilder ? (
            <button
              data-testid={`product-configure-${product.slug}`}
              onClick={() => navigate(`/product/${product.slug}`)}
              className={`flex items-center gap-1.5 border ${accent.border} px-3 py-1.5 font-display text-xs font-bold uppercase tracking-widest ${accent.text} transition-colors hover:${accent.bg} hover:text-ink`}
            >
              <SlidersHorizontal className="h-3.5 w-3.5" /> Configure
            </button>
          ) : (
            <button
              data-testid={`product-add-${product.slug}`}
              onClick={handleAdd}
              className="flex items-center gap-1.5 border border-white/20 px-3 py-1.5 font-display text-xs font-bold uppercase tracking-widest text-white transition-colors hover:border-brand-yellow hover:bg-brand-yellow hover:text-ink"
            >
              <Plus className="h-3.5 w-3.5" /> Add
            </button>
          )}
        </div>
      </div>
    </motion.article>
  );
};

export default ProductCard;
